// InventoryServices/InventoryInfoTabs/rsformDataMapper.js
//
// Middleware: rsform_data → form field keys
//
// V1 form (title: "floor-plan")    → snake_case keys
// V2 form (title: "floor-plan-v2") → camelCase keys (mostly same as rsform_data)

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

// "50000 lakh", "500000 lakh" → "50000", "500000"
const extractNumber = (val) => {
  if (val === undefined || val === null || val === "") return undefined;
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ""));
  return isNaN(num) ? undefined : String(num);
};

// Array → first element string (for text fields like referenceImage, attachmentUri)
const arrayToString = (val) => {
  if (Array.isArray(val)) return val[0] || "";
  return val;
};

// ─────────────────────────────────────────────────────────────────
// VASTU value map — rsform uses underscore, form uses spaces
// ─────────────────────────────────────────────────────────────────
const VASTU_VALUE_MAP = {
  yes:                   "yes",
  no:                    "no",
  as_much_as_possible:   "as much as possible",
};

// ─────────────────────────────────────────────────────────────────
// Side plot value map
// Section 2 (right_side_plot etc): "road" / "neighbour"
// Section 3 (inRight etc):         "road" / "Neighbour Plot"
// ─────────────────────────────────────────────────────────────────
const toSection2SidePlot = (val) => {
  if (!val || val === "") return undefined;
  if (val === "Road") return "road";
  if (val === "Neighbour Plot") return "neighbour";
  return val;
};

const toSection3SidePlot = (val) => {
  if (!val || val === "") return undefined;
  if (val === "Road") return "road";
  if (val === "Neighbour Plot") return "Neighbour Plot";
  return val;
};

// ─────────────────────────────────────────────────────────────────
// V1 mapping
// ─────────────────────────────────────────────────────────────────
const V1_KEY_MAP = {
  shape:                    "plot_shape",
  inRight:                  "right_side_plot",
  inBack:                   "back_side_plot",
  inLeft:                   "left_side_plot",
  projectType:              "project_type",
  setbacksFront:            "setback_front",
  setbacksBack:             "setback_back",
  setbacksLeft:             "setback_left",
  setbacksRight:            "setback_right",
  levelRelation:            "plot_level",
  levelDelta:               "plot_level_difference",
  staircaseType:            "staircase_type",
  parking:                  "parking_type",
  vastuPriority:            "vastu_preference",
  masterBedroomsWithToilet: "master_bedrooms",
  externalWallThickness:    "external_wall_thickness",
  internalWallThickness:    "internal_wall_thickness",
  plinthLevel:              "plinth_level",
  lintelLevel:              "lintel_level",
  sillLevel:                "sill_level",
  slabHeight:               "slab_height",
  stairTread:               "tread",
  stairRiser:               "riser",
  slabExtension:            "slab_extension",
  slabSides:                "slab_extension_sides",
  waterTank:                "water_tank",
  septicTank:               "septic_tank",
  direction:                "direction",
  floors:                   "floors",
  width:                    "width",
  depth:                    "depth",
  borewell:                 "borewell",
  trees:                    "trees",
};

const V1_VALUE_MAP = {
  plot_level: {
    same:  "Same as Road",
    above: "Above Road",
    below: "Below Road",
  },
  staircase_type: {
    External: "external",
    Internal: "internal",
    Both:     "both",
  },
};

// ─────────────────────────────────────────────────────────────────
// V2: keys to skip from passthrough (handled separately)
// ─────────────────────────────────────────────────────────────────
const V2_SKIP_PASSTHROUGH = new Set([
  // handled with custom logic
  "kitchen", "kitchenType",
  "vastuPriority",
  "parking",
  "approximateBudget",
  "referenceImage",
  "attachmentUri",
  "inRight", "inBack", "inLeft",  // side plots — dual mapping needed
  // meta keys
  "requirementForfloors", "inventory_id", "projectID", "version",
  "updatedAt", "postedBy", "postedByType", "createdAt", "_id",
  // floorReq boolean keys → go into additional_spaces
  "drawingRoom", "shop", "poojaRoom", "Balcony", "livingRoom", "recreationArea",
  "office", "foyer", "masterBedrooms", "dressingArea",
  "theatre", "washArea", "diningArea", "studyRoom", "gym", "garden",
  "attachedToilet", "floorNo",
  // extra rsform keys not in V2 form
  "setbacksLeft", "setbacksRight", "setbacksBack", "setbacksFront",
  "setbacksMidRight", "plotOffset", "attachPlotImage", "plotRightDepth",
  "plotBackWidth", "diagonal1", "diagonal2", "inFront",
]);

// ─────────────────────────────────────────────────────────────────
// V2 passthrough value conversions
// ─────────────────────────────────────────────────────────────────
const V2_PASSTHROUGH_VALUE_MAP = {
  staircaseType: { External: "external", Internal: "internal", Both: "both" },
  // FIX: purane saved records me "shape" field ki value "irregular" /
  // "regular" ke naam se save hui thi, lekin current v2 form ke radio
  // options sirf "rectangular" / "non_rectangular" accept karte hain.
  // Isi mismatch ki wajah se radio kabhi checked nahi dikhta tha
  // (value save thi, bas option ke value se match nahi hoti thi).
  // Ab legacy value ko current option-value me convert kar rahe hain.
  // Agar value already "rectangular"/"non_rectangular" hai (naya format),
  // to ye map usse touch nahi karta — passthrough hi hota hai.
  shape: { irregular: "non_rectangular", regular: "rectangular" },
};

// ─────────────────────────────────────────────────────────────────
// additional_spaces — from requirementForfloors[0] booleans
// ─────────────────────────────────────────────────────────────────
const FLOOR_REQ_TO_ADDITIONAL_SPACES = {
  livingRoom:  "living room",
  poojaRoom:   "pooja room",
  drawingRoom: "drawing room",
  diningArea:  "dining area",
  studyRoom:   "study room",
  gym:         "gym room",
  washArea:    "utility/wash area",
};

// ─────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────
export const mapRsformDataToFormValues = (rsformData, formTitle = "") => {
  if (!rsformData) return {};

  const isV2 = formTitle === "floor-plan-v2";

  // Always use requirementForfloors[0] for floor-level fields
  const floorReq =
    Array.isArray(rsformData.requirementForfloors) &&
    rsformData.requirementForfloors.length > 0
      ? rsformData.requirementForfloors[0]
      : {};

  // top-level takes priority over floorReq
  const rawData = { ...floorReq, ...rsformData };

  const result = {};

  if (isV2) {
    // ── V2 MAPPER ─────────────────────────────────────────────

    // 1. Passthrough everything except skipped keys
    Object.entries(rawData).forEach(([key, val]) => {
      if (V2_SKIP_PASSTHROUGH.has(key)) return;
      if (val === undefined || val === null) return;
      const valMap = V2_PASSTHROUGH_VALUE_MAP[key];
      result[key] = valMap ? (valMap[val] ?? val) : val;
    });

    // 2. vastu_preference — value needs underscore→spaces conversion
    if (rawData.vastuPriority !== undefined) {
      result["vastu_preference"] = VASTU_VALUE_MAP[rawData.vastuPriority] ?? rawData.vastuPriority;
    }

    // 3. parking_type
    if (rawData.parking !== undefined) {
      result["parking_type"] = rawData.parking;
    }

    // 4. approximate_budget — strip "lakh" text, keep number only
    if (rawData.approximateBudget) {
      const cleaned = extractNumber(rawData.approximateBudget);
      if (cleaned !== undefined) result["approximate_budget"] = cleaned;
    }

    // 5. Kitchen logic:
    //    floorReq.kitchen = "true"/"false" → form kitchen = "yes"/"no"
    //    floorReq.kitchenType = "open"/"closed" → form kitchenType = same
    const kitchenVal = floorReq.kitchen ?? rawData.kitchen;
    if (kitchenVal === "true" || kitchenVal === true) {
      result["kitchen"] = "yes";
    } else if (kitchenVal === "false" || kitchenVal === false) {
      result["kitchen"] = "no";
    } else if (kitchenVal === "Open" || kitchenVal === "Closed") {
      // old format fallback
      result["kitchen"] = "yes";
    }
    // kitchenType — from floorReq directly (already "open"/"closed")
    if (floorReq.kitchenType) {
      result["kitchenType"] = floorReq.kitchenType;
    } else if (typeof kitchenVal === "string" && (kitchenVal === "Open" || kitchenVal === "Closed")) {
      result["kitchenType"] = kitchenVal.toLowerCase();
    }

    // 6. Side plots — dual mapping
    //    inRight/inBack/inLeft → Section 3 (inRight/inBack/inLeft) as-is
    //                          → Section 2 (right_side_plot etc) with value conversion
    const sideKeys = {
      inRight: "right_side_plot",
      inBack:  "back_side_plot",
      inLeft:  "left_side_plot",
    };
    Object.entries(sideKeys).forEach(([rsKey, s2Key]) => {
      const val = rawData[rsKey];
      // Section 3: pass original value
      const s3Val = toSection3SidePlot(val);
      if (s3Val !== undefined) result[rsKey] = s3Val;
      // Section 2: convert to "road"/"neighbour"
      const s2Val = toSection2SidePlot(val);
      if (s2Val !== undefined) result[s2Key] = s2Val;
    });

    // 7. referenceImage / attachmentUri — array → string
    if (rawData.referenceImage) {
      result["referenceImage"] = arrayToString(rawData.referenceImage);
    }
    if (rawData.attachmentUri) {
      result["attachmentUri"] = arrayToString(rawData.attachmentUri);
    }

    // 8. AnySpecificRequirement → other_space (text field)
    if (floorReq.AnySpecificRequirement) {
      result["other_space"] = floorReq.AnySpecificRequirement;
    }

  } else {
    // ── V1 MAPPER ─────────────────────────────────────────────

    Object.entries(V1_KEY_MAP).forEach(([rsKey, formKey]) => {
      const rawVal = rawData[rsKey];
      if (rawVal === undefined || rawVal === null || rawVal === "") return;

      const valueMap = V1_VALUE_MAP[formKey];
      const mappedVal = valueMap ? (valueMap[rawVal] ?? rawVal) : rawVal;
      result[formKey] = mappedVal;
    });

    // vastu_preference
    if (rawData.vastuPriority) {
      result["vastu_preference"] = VASTU_VALUE_MAP[rawData.vastuPriority] ?? rawData.vastuPriority;
    }

    // approximate_budget
    if (rawData.approximateBudget) {
      const cleaned = extractNumber(rawData.approximateBudget);
      if (cleaned !== undefined) result["approximate_budget"] = cleaned;
    }

    // Kitchen
    const kitchenVal = floorReq.kitchen ?? rawData.kitchen;
    if (kitchenVal === "true" || kitchenVal === true || kitchenVal === "Open") {
      result["kitchen"] = "yes";
      result["kitchen_type"] = floorReq.kitchenType ?? (kitchenVal === "Open" ? "open" : undefined);
    } else if (kitchenVal === "false" || kitchenVal === false) {
      result["kitchen"] = "no";
    }

    // V1 side plots
    ["right_side_plot", "back_side_plot", "left_side_plot"].forEach((formKey) => {
      if (result[formKey] !== undefined) {
        const s2Val = toSection2SidePlot(result[formKey]);
        if (s2Val !== undefined) result[formKey] = s2Val;
        else delete result[formKey];
      }
    });
  }

  // ── additional_spaces: same for V1 and V2
  const additionalSpaces = [];
  Object.entries(FLOOR_REQ_TO_ADDITIONAL_SPACES).forEach(([reqKey, spaceVal]) => {
    const val = floorReq[reqKey];
    if (val === "true" || val === true) {
      additionalSpaces.push(spaceVal);
    }
  });
  if (additionalSpaces.length > 0) {
    result["additional_spaces"] = additionalSpaces;
  }

  return result;
};